import { useState } from 'react';
import { signIn } from "supertokens-web-js/recipe/emailpassword";
import { useTenantNavigation } from '../navigation/useTenantNavigation';
import { getTenantIdFromStorage } from '../../services/authentication/supertokens';
import { joinOrganization } from '../../services/authentication/organizationService';

interface JoinFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  accessCode: string;
}

export const useOrganizationJoin = () => {
  const [formData, setFormData] = useState<JoinFormData>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    accessCode: ''
  });
  const [error, setError] = useState<string>('');
  const { navigateWithTenant } = useTenantNavigation();
  const tenantId = getTenantIdFromStorage();

  const handleInputChange = (field: keyof JoinFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = (): boolean => {
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (!/[a-z]/.test(formData.password)) {
      setError('Password must contain at least one lowercase letter');
      return false;
    }
    if (!/[A-Z]/.test(formData.password)) {
      setError('Password must contain at least one uppercase letter');
      return false;
    }
    if (!/\d/.test(formData.password)) {
      setError('Password must contain at least one number');
      return false;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
      setError('Password must contain at least one special character');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    if (!tenantId) {
      setError('Organization ID not found');
      return;
    }

    try {
      const response = await joinOrganization(tenantId, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        accessCode: formData.accessCode
      });

      localStorage.setItem('tenantId', response.tenantId.toLowerCase());

      const signInResponse = await signIn({
        formFields: [{
          id: "email",
          value: formData.email
        }, {
          id: "password",
          value: formData.password
        }]
      });

      if (signInResponse.status === "OK") {
        navigateWithTenant('/dashboard');
      } else {
        setError('Failed to sign in after signup');
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === 'Email already exists') {
          setError('This email is already registered in this organization. Please login instead.');
        } else if (error.message === 'Organization not found') {
          setError('This organization does not exist.');
        } else if (error.message === 'Invalid access code') {
          setError('The access code you provided is incorrect.');
        } else {
          setError(error.message);
        }
      } else {
        setError("Oops! Something went wrong.");
      }
    }
  };

  return {
    formData,
    error,
    handleInputChange,
    handleSubmit
  };
}; 